// RequestQueue: Manages concurrent API requests to prevent Lambda throttling
class RequestQueue {
    constructor(maxConcurrent = 15) {
        this.queue = [];
        this.running = 0;
        this.maxConcurrent = maxConcurrent;
        this.requests = new Map(); // Track active requests for debugging
    }

    async add(requestFn, requestId = null) {
        const id = requestId || `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
        // Log if we need to wait
        if (this.running >= this.maxConcurrent) {
            // console.log(`🚦 RequestQueue: Request ${id} waiting (${this.running}/${this.maxConcurrent} running, ${this.queue.length} in queue)`);
        }
        
        // Wait if we're at max capacity
        while (this.running >= this.maxConcurrent) {
            await new Promise(resolve => this.queue.push(resolve));
        }

        this.running++;
        
        const startTime = Date.now();
        try {
            this.requests.set(id, { startTime, status: 'running' });
            const result = await requestFn();
            const duration = Date.now() - startTime;
            
            // Only log if request was slow (over 3 seconds)
            if (duration > 3000) {
                // console.log(`⏱️  RequestQueue: Slow request ${id} took ${duration}ms`);
            }
            this.requests.set(id, { startTime, status: 'completed', duration });
            return result;
        } catch (error) {
            const duration = Date.now() - startTime;
            // console.log(`❌ RequestQueue: Failed ${id} after ${duration}ms - ${error.message}`);
            this.requests.set(id, { startTime, status: 'failed', error, duration });
            throw error;
        } finally {
            this.running--;
            this.requests.delete(id);
            
            // Process waiting requests
            if (this.queue.length > 0) {
                // Only log if there's a significant queue
                if (this.queue.length > 2) {
                    // console.log(`🔄 RequestQueue: ${this.queue.length} requests still waiting in queue`);
                }
                const resolve = this.queue.shift();
                resolve();
            }
        }
    }

    // Get current queue status
    getStatus() {
        return {
            running: this.running,
            queued: this.queue.length,
            maxConcurrent: this.maxConcurrent,
            activeRequests: Array.from(this.requests.entries())
        };
    }

    // Clear the queue (useful for cleanup)
    clear() {
        this.queue.forEach(resolve => resolve());
        this.queue = [];
    }
}

// Create singleton instance for the application
const globalRequestQueue = new RequestQueue(15);

export { RequestQueue, globalRequestQueue };